import React, { LegacyRef, MouseEventHandler, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { WIDGET_TYPES } from '../../../util/constants';
import Button from './widgets/Button';
import { IconNames } from '../../MuiIcon/Icon';
import Image from './widgets/Image';
import Icon from './widgets/Icon';
import Text from './widgets/Text';
import Hotspot from './widgets/Hotspot';
import Iframe from './widgets/Iframe';

export interface StyleProps {
	dimension: {
		w: number;
		h: number;
	};
	coordinate: {
		x: number;
		y: number;
	};
	rotation: number;
	textLabel: {
		text: string;
		backgroundColor: string;
		color: string;
		fontWeight: number;
		hoverBackgroundColor: string;
		hoverFontWeight: number;
		hoverColor: string;
		font: string;
		fontSize: number;
		lineHeight: string;
		shadowColor: string;
	};
	headerLabel: {
		text: string;
		backgroundColor: string;
		color: string;
		fontWeight: number;
		hoverBackgroundColor: string;
		hoverFontWeight: number;
		hoverColor: string;
		font: string;
		fontSize: number;
		lineHeight: string;
		shadowColor: string;
	};
	transformSettings: {
		paddingBottom: string;
		paddingLeft: string;
		paddingRight: string;
		paddingTop: string;
		hoverPaddingBottom: string;
		hoverPaddingLeft: string;
		hoverPaddingRight: string;
		hoverPaddingTop: string;
		radiusBottom: string; // Use for Text widget only
		radiusLeft: string; // Use for Text widget only
		radiusRight: string; // Use for Text widget only
		radiusTop: string; // Use for Text widget only
		hoverRadiusTop: string;
		hoverRadiusRight: string;
		hoverRadiusBottom: string;
		hoverRadiusLeft: string;
		borderRadiusLeft: string;
		borderRadiusTop: string;
		borderRadiusRight: string;
		borderRadiusBottom: string;
	};
	backgroundSettings: {
		backgroundColor: string;
		borderType: string;
		borderWidth: string;
		borderColor: string;
		hoverBackgroundColor: string;
		hoverBorderType: string;
		hoverBorderWidth: string;
		hoverBorderColor: string;
	};
	iconConfig?: { position: string; size: string; spacing: string };
	textAlignment: string;
	contentSpacing: string;
}
interface WidgetInteractionBase {
	id: string;
	style: StyleProps;
	data: { type: string; settings: { iconKey?: IconNames; imgUrl: string; bgImgUrl?: string }; name: string };
	clickable: boolean;
	scenarioId: string;
	sceneId: string;
	onClick: (_event: object) => {};
	isMobile?: boolean;
}

interface WidgetInteractionItem extends WidgetInteractionBase {
	end: number;
	position: number;
	onEnter?: () => void;
	onExit?: () => void;
}

export interface DetailWidgetInteractionItem extends WidgetInteractionBase {
	isHover: boolean;
	widgetRef: LegacyRef<SVGForeignObjectElement>;
	handleMouseEnter?: MouseEventHandler<HTMLDivElement> | MouseEventHandler<SVGImageElement> | MouseEventHandler<SVGGElement>;
	handleMouseLeave?: MouseEventHandler<HTMLDivElement> | MouseEventHandler<SVGImageElement> | MouseEventHandler<SVGGElement>;
	position?: number;
	onEnter?: () => void;
	onExit?: () => void;
}

const createWidget = (props: DetailWidgetInteractionItem) => {
	const {
		data: { type },
	} = props;

	switch (type) {
		case WIDGET_TYPES.WIDGET_TYPE_BUTTON:
			return <Button {...props} key={props.id} />;
		case WIDGET_TYPES.WIDGET_TYPE_IMAGE:
			return <Image {...props} key={props.id} />;
		case WIDGET_TYPES.WIDGET_TYPE_ICON:
			return <Icon {...props} key={props.id} />;
		case WIDGET_TYPES.WIDGET_TYPE_TEXT:
			return <Text {...props} key={props.id} />;
		case WIDGET_TYPES.WIDGET_TYPE_HOTSPOT:
			return <Hotspot {...props} key={props.id} />;
		case WIDGET_TYPES.WIDGET_TYPE_IFRAME:
			return <Iframe {...props} key={props.id} />;
		default:
			return <></>;
	}
};

const WidgetElement = ({
	id,
	style,
	data,
	clickable,
	end,
	position,
	scenarioId,
	sceneId,
	isMobile,
	onClick,
	onEnter,
	onExit,
}: WidgetInteractionItem) => {
	const [isHover, setIsHover] = useState(false);
	const [isTriggeredOnExitEvent, setTriggeredOnExitEvent] = useState(false);
	const widgetRef = useRef<SVGForeignObjectElement>(null);

	const handleMouseEnter = isMobile
		? undefined
		: () => {
				setIsHover(true);
			};

	const handleMouseLeave = isMobile
		? undefined
		: () => {
				setIsHover(false);
			};

	useLayoutEffect(() => {
		onEnter?.();
	}, []);

	useEffect(() => {
		if (end - position <= 1 && !isTriggeredOnExitEvent) {
			onExit?.();
			setTriggeredOnExitEvent(true);
		}
	}, [position, end, isTriggeredOnExitEvent]);

	return createWidget({
		id,
		data,
		style,
		clickable,
		isHover,
		widgetRef,
		scenarioId,
		sceneId,
		onClick,
		position,
		handleMouseEnter,
		handleMouseLeave,
	});
};

export default WidgetElement;
